/**
 * Standardized error state component
 * Provides consistent error UI across the application
 */

import React from 'react';
import {
  Box,
  Alert,
  Button,
  AlertTitle,
  Typography
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logger } from '../../utils/logger';
import { formatUserMessage } from '../../utils/errorHandler';

const ErrorState = ({
  error,
  title,
  message,
  context = '',
  onRetry,
  retryText = 'Try Again',
  showIcon = true,
  severity = 'error',
  component = 'ErrorState',
  fullHeight = false,
  additionalInfo = null
}) => {
  // Set default title based on severity if not provided
  const defaultTitle = title || (severity === 'success' ? 'Success' : severity === 'warning' ? 'Warning' : 'Error');
  // Format error message if not provided
  const displayMessage = message || formatUserMessage(error, context);

  React.useEffect(() => {
    logger.error(component, 'Error state displayed', {
      error: error?.message || error,
      context,
      displayMessage,
      additionalInfo
    });
  }, [error, context, displayMessage, component, additionalInfo]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullHeight ? '400px' : 'auto',
        p: 3
      }}
    >
      <Alert
        severity={severity}
        icon={showIcon ? <ErrorOutlineIcon fontSize="large" /> : false}
        sx={{
          width: '100%',
          maxWidth: 600,
          '& .MuiAlert-icon': {
            fontSize: '2rem'
          }
        }}
        action={
          onRetry && (
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                logger.info(component, 'Retry button clicked', { context });
                onRetry();
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {retryText}
            </Button>
          )
        }
      >
        {defaultTitle && <AlertTitle>{defaultTitle}</AlertTitle>}
        <Typography variant="body2">
          {displayMessage}
        </Typography>
        {additionalInfo && (
          <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="caption" component="pre" style={{ whiteSpace: 'pre-wrap' }}>
              {additionalInfo}
            </Typography>
          </Box>
        )}
      </Alert>
    </Box>
  );
};

export default ErrorState;
